import { useCallback, useMemo, useState } from 'react';

export interface RowSelection {
  selectedIds: string[];
  count: number;
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  clear: () => void;
  /** Narrow the selection to those of `ids` already in it -- the rows a bulk action left behind. */
  keepOnly: (ids: string[]) => void;
  /** Every visible row is ticked. */
  allVisibleSelected: boolean;
  /** Some but not all of them -- drives the header tick's dashed state. */
  someVisibleSelected: boolean;
  toggleAllVisible: () => void;
  /** Ticked rows the current search or status filter is hiding. */
  hiddenCount: number;
}

/**
 * Checkbox selection for a table whose rows come and go with search and filters.
 *
 * The selection deliberately survives a filter change. Someone thinning out a
 * catalogue searches, ticks a few, searches again, ticks a few more -- dropping
 * the earlier ticks every time would make the feature useless. What it must not
 * do is act on rows the person cannot see without saying so, which is what
 * `hiddenCount` is for: the action bar states it and the confirm dialog counts
 * those rows in.
 *
 * Pass a memoised `visibleIds`, or every render rebuilds the lookup.
 */
export function useRowSelection(visibleIds: string[]): RowSelection {
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set<string>());

  const visible = useMemo(() => new Set(visibleIds), [visibleIds]);

  const isSelected = useCallback((id: string) => selected.has(id), [selected]);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      // delete() reports whether it removed anything, so this is one lookup.
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set<string>()), []);

  const keepOnly = useCallback((ids: string[]) => {
    setSelected((prev) => new Set(ids.filter((id) => prev.has(id))));
  }, []);

  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someVisibleSelected =
    !allVisibleSelected && visibleIds.some((id) => selected.has(id));

  // Ticks rows outside the filter neither gain nor lose: the header tick speaks
  // for what is on screen, and nothing else.
  const toggleAllVisible = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      const everyone = visibleIds.every((id) => next.has(id));
      for (const id of visibleIds) {
        if (everyone) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }, [visibleIds]);

  const hiddenCount = useMemo(
    () => [...selected].filter((id) => !visible.has(id)).length,
    [selected, visible],
  );

  const selectedIds = useMemo(() => [...selected], [selected]);

  return {
    selectedIds,
    count: selected.size,
    isSelected,
    toggle,
    clear,
    keepOnly,
    allVisibleSelected,
    someVisibleSelected,
    toggleAllVisible,
    hiddenCount,
  };
}
